import { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { fetchGames, GAMES_QUERY_KEYS, type Game } from '@/features/games/api.js';
import { useGameLaunch } from '@/features/games/useGameLaunch.js';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog.js';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: Props) {
  const { t, i18n } = useTranslation('common');
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const gamesQuery = useQuery({
    queryKey: GAMES_QUERY_KEYS.list({}),
    queryFn: () => fetchGames({ pageSize: 200 }),
    staleTime: 60_000,
    enabled: open,
  });

  const { launch, modal } = useGameLaunch({ lang: i18n.language ?? 'th' });

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return (gamesQuery.data?.items ?? [])
      .filter((g) => g.name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [gamesQuery.data, query]);

  // Reset query when palette opens.
  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  async function handleSelect(g: Game) {
    if (!g.providerId) return;
    onClose();
    await launch(g);
  }

  return (
    <>
      {/* Launch modal stays mounted regardless of `open` so a game selected
          from the palette still renders after the overlay closes. */}
      {modal}
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v) onClose();
        }}
      >
        <DialogContent
          hideClose
          aria-label={t('common.search')}
          className="top-[15vh] max-w-lg -translate-y-0 gap-0 overflow-hidden p-0"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <DialogTitle className="sr-only">{t('common.search')}</DialogTitle>

          {/* Input row */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--border-subtle)]">
            <Search className="size-4 shrink-0 text-[var(--text-muted)]" aria-hidden />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('common.search')}
              className="flex-1 bg-transparent text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none"
            />
          </div>

          {/* Results */}
          {query.trim() && (
            <ul className="max-h-[60vh] overflow-y-auto py-2">
              {results.length === 0 ? (
                <li className="px-4 py-3 text-[13px] text-[var(--text-muted)]">
                  {t('common.noResults')}
                </li>
              ) : (
                results.map((g) => (
                  <li key={g.id}>
                    <button
                      type="button"
                      onClick={() => void handleSelect(g)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[var(--accent)]/10 transition-colors group"
                    >
                      {g.thumbnailUrl || g.imageUrl ? (
                        <img
                          src={(g.thumbnailUrl ?? g.imageUrl)!}
                          alt=""
                          className="w-8 h-8 rounded object-cover shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded bg-[var(--bg-elevated)] shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-[var(--text-primary)] truncate group-hover:text-[var(--accent)]">
                          {g.name}
                        </p>
                        {g.providerDisplayName && (
                          <p className="text-[11px] text-[var(--text-muted)] truncate">
                            {g.providerDisplayName}
                          </p>
                        )}
                      </div>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
